/**
 * Static Business Data Exports
 * Provides typed access to consolidated ESSCO catalog data and utility functions
 */

// Import the consolidated catalog data
import esscoProductsData from './essco_products.json' with { type: 'json' }

// Type definitions for the consolidated ESSCO catalog data
export interface EsscoProduct {
  id: string
  name: string
  description: string
  unit: string
  qtyPerUnit: number          // New field: quantity per unit of sale
  pricePerUnit: number
  perItem: number             // New field: price per individual item
  orders: number              // New field: order count/frequency
  source: string              // New field: source of pricing data
  category: string
  imageUrl?: string           // New field: product image URL
  isLegacyProduct?: boolean   // New field: indicates if this is a legacy product
}

// Convert API products to catalog items for order system compatibility
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const convertToCatalogItem = (product: any): EsscoProduct => {
  // For enhanced products (API products with historical data)
  if (product.historicalData) {
    return {
      id: product.id,
      name: product.name,
      description: product.description,
      unit: product.historicalData.unit,
      qtyPerUnit: product.historicalData.qtyPerUnit,
      pricePerUnit: product.historicalData.pricePerUnit,
      perItem: product.historicalData.perItem,
      orders: product.historicalData.orders,
      source: product.historicalData.source,
      category: product.historicalData.category,
      imageUrl: product.images?.[0]?.url, // Get first image URL
      isLegacyProduct: false
    }
  }
  
  // For legacy products (invoice-only products)
  if (product.isLegacyProduct && product.historicalData) {
    return {
      id: product.id,
      name: product.name,
      description: product.description,
      unit: product.historicalData.unit,
      qtyPerUnit: product.historicalData.qtyPerUnit,
      pricePerUnit: product.historicalData.pricePerUnit,
      perItem: product.historicalData.perItem,
      orders: product.historicalData.orders,
      source: product.historicalData.source,
      category: product.historicalData.category,
      imageUrl: undefined, // Legacy products don't have images
      isLegacyProduct: true
    }
  }
  
  // For API-only products (no historical data), create reasonable defaults
  return {
    id: product.id,
    name: product.name,
    description: product.description,
    unit: 'EA', // Default unit
    qtyPerUnit: 1, // Default quantity per unit
    pricePerUnit: product.price || 0,
    perItem: product.price || 0,
    orders: 0, // No order history
    source: 'API', // Source is API
    category: product.categories?.[0]?.name || 'general', // Use first category or default
    imageUrl: product.images?.[0]?.url, // Get first image URL
    isLegacyProduct: false
  }
}

// Convert all products to catalog items for backward compatibility
export const esscoCatalog: EsscoProduct[] = esscoProductsData.products.map(convertToCatalogItem)

/**
 * Get product price by product ID
 * @param productId - ESSCO product ID
 * @returns Unit price or null if not found
 */
export const getProductPrice = (productId: string): number | null => {
  const product = esscoCatalog.find(p => p.id === productId)
  return product?.pricePerUnit ?? null
}

/**
 * Get product by ID
 * @param productId - ESSCO product ID
 * @returns Product or null if not found
 */
export const getProductById = (productId: string): EsscoProduct | null => {
  return esscoCatalog.find(p => p.id === productId) ?? null
}

/**
 * Search products by name, description, or category
 * @param query - Search term
 * @returns Array of matching products
 */
export const searchProducts = (query: string): EsscoProduct[] => {
  const lowerQuery = query.toLowerCase()
  return esscoCatalog.filter(product => 
    product.name.toLowerCase().includes(lowerQuery) ||
    product.description.toLowerCase().includes(lowerQuery) ||
    product.category.toLowerCase().includes(lowerQuery)
  )
}

/**
 * Get products by category
 * @param category - Product category
 * @returns Array of products in category
 */
export const getProductsByCategory = (category: string): EsscoProduct[] => {
  return esscoCatalog.filter(product => 
    product.category.toLowerCase() === category.toLowerCase()
  )
}

/**
 * Get all unique categories
 * @returns Array of category names
 */
export const getCategories = (): string[] => {
  const categories = new Set(esscoCatalog.map(p => p.category))
  return Array.from(categories).sort()
}

/**
 * Calculate line total for order item
 * @param productId - ESSCO product ID
 * @param quantity - Quantity ordered
 * @returns Total price or null if product not found
 */
export const calculateLineTotal = (productId: string, quantity: number): number | null => {
  const unitPrice = getProductPrice(productId)
  return unitPrice ? unitPrice * quantity : null
}

/**
 * Get all products
 * @returns Array of all products
 */
export const getAllProducts = (): EsscoProduct[] => {
  return [...esscoCatalog]
} 